import Animated, { SharedValue, useAnimatedStyle } from "react-native-reanimated";
import { NonOptional } from "src/shared/types/general";
import { InfiniteCarouselProps } from "../types";

const InfiniteCarouselItem = <TItem extends any>({
	renderItem,
	item,
	itemDim,
	animatedIndex,
	index,
	itemSpacing,
	isFocused,
	curveFactor,
	scaleFactor,
	opacityFactor,
	animatedStyleWorklet,
}: NonOptional<
	Pick<
		InfiniteCarouselProps<TItem>,
		"renderItem" | "itemDim" | "itemSpacing" | "curveFactor" | "scaleFactor" | "opacityFactor"
	>
> &
	Pick<InfiniteCarouselProps<TItem>, "animatedStyleWorklet"> & {
		item: TItem;
		index: number;
		animatedIndex: SharedValue<number>;
		isFocused: boolean;
	}) => {
	const animatedStyle = useAnimatedStyle(() => {
		const indexDiff = animatedIndex.value - index;
		if (animatedStyleWorklet) {
			return animatedStyleWorklet({
				indexDiff,
				itemDim,
				index,
				animatedIndex,
				itemSpacing,
				curveFactor,
				scaleFactor,
				opacityFactor,
			});
		}
		return {
			transform: [
				{
					rotate: `${indexDiff * -curveFactor}rad`,
				},
				{
					translateX: -(animatedIndex.value - index) * (itemDim.width + itemSpacing),
				},
				{
					scale: 1 - Math.min(scaleFactor, Math.abs(indexDiff) * scaleFactor),
				},
			],
			opacity: 1 - Math.min(opacityFactor, Math.abs(indexDiff) * opacityFactor),
		};
	}, [
		animatedIndex,
		curveFactor,
		index,
		itemDim,
		itemSpacing,
		opacityFactor,
		scaleFactor,
		animatedStyleWorklet,
	]);

	return (
		<Animated.View
			style={[
				{
					position: "absolute",
					width: itemDim.width,
					height: itemDim.height,
				},
				animatedStyle,
			]}
		>
			{renderItem({
				item,
				width: itemDim.width,
				height: itemDim.height,
				isFocused,
			})}
		</Animated.View>
	);
};
export default InfiniteCarouselItem;
